import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TowerControl } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to the tower");
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-200 dark:bg-slate-950 grid lg:grid-cols-2">
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1601000785676-f9b0ade234d3?crop=entropy&cs=srgb&fm=jpg&q=85)",
        }}
      >
        <div className="absolute inset-0 bg-white/85 dark:bg-slate-950/80" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <h2 className="text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-tight">
            Every brand message,
            <br />
            one control tower.
          </h2>
          <p className="mt-3 text-base text-slate-600 dark:text-slate-400 max-w-md">
            Price lists, schemes, ledgers and GST notices — collapsed into structured business events with impact
            flags and permanent vendor memory.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="login-page">
          <div className="flex items-center gap-3 mb-8">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300 dark:border-emerald-500/40">
              <TowerControl className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-base font-extrabold tracking-tight text-slate-900 dark:text-slate-100 leading-none">SHD CONTROL TOWER</div>
              <div className="text-sm font-mono uppercase tracking-widest text-slate-600 dark:text-slate-400">Sethi Home Decor</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Owner sign in</h1>
          <p className="mt-1 text-base text-slate-600 dark:text-slate-400">Open your morning brief.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            {error && (
              <div data-testid="login-error" className="rounded-md border border-rose-300 dark:border-rose-500/40 bg-rose-50 dark:bg-rose-500/10 px-3 py-2 text-base text-rose-700 dark:text-rose-300">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
                placeholder="you@sethihomedecor.com"
              />
            </div>
            <div>
              <label className="block text-sm font-mono uppercase tracking-wider text-slate-600 dark:text-slate-400 mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-base text-slate-900 dark:text-slate-100 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-form-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-md bg-emerald-500 px-4 py-2.5 text-base font-bold text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-center">
              <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                Forgot password?
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
